import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { BookJob } from 'src/providers/book-job/book-job';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { Loading } from 'src/shared/components/loading/loading';
import { WindowService } from 'src/shared/providers/window.service';

@Component({
    templateUrl: './service-rename.html',
    standalone: false
})

export class ServiceRenamePage implements OnInit, OnDestroy {

  dialogParams: any;
  errorMessage: string;
  form: UntypedFormGroup;
  isRightSideContent: boolean;
  loaded: boolean;
  services: any[];
  submitted: boolean;
  private subscriptions: Subscription[] = [];
  private initialValues: Map<number, string> = new Map();

constructor(
    private bookJob: BookJob,
    private fb: UntypedFormBuilder,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private util: Util,
    public windowService: WindowService
  ) {
    this.form = this.fb.group({});
  }

  get isMobile(): boolean {
    return !this.windowService.isDesktopRes;
  }

  async ngOnInit() {
    this.loaded = false;
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.rightSidePanelService.setDialogPageTitle('Edit Service Names');
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    await this.loadServices();
    this.loaded = true;
  }

  @Loading('', true)
  async loadServices() {
    try {
      const services = await this.bookJob.fetchAllRequiredTeamServices();
      this.services = services.map(service => {
        const originalName = service.original_name || service.name;
        const currentName = service.name || service.original_name;
        const hasCustomName = currentName !== originalName;

        return {
          id: service.id,
          key: service.key,
          originalName,
          customName: hasCustomName ? currentName : '',
          name: currentName
        };
      });

      // Create form controls for each service
      const formControls = {};
      this.services.forEach(service => {
        const initialValue = service.customName || '';
        formControls[`service_${service.id}`] = [initialValue];
        this.initialValues.set(service.id, initialValue);
      });
      this.form = this.fb.group(formControls);

      // Subscribe to form control changes for auto-save
      this.setupAutoSave();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  setupAutoSave() {
    this.services.forEach(service => {
      const controlName = `service_${service.id}`;
      const control = this.form.get(controlName);

      if (control) {
        const subscription = control.valueChanges
          .pipe(
            debounceTime(800),
            distinctUntilChanged()
          )
          .subscribe(async (value: string) => {
            const trimmedValue = (value || '').trim();
            const initialValue = this.initialValues.get(service.id) || '';

            // Only save if the value has actually changed
            if (trimmedValue !== initialValue) {
              await this.updateServiceName(service.id, true);
              // Update initial value after successful save
              this.initialValues.set(service.id, trimmedValue);
            }
          });

        this.subscriptions.push(subscription);
      }
    });
  }

  async updateServiceName(serviceId: number, isAutoSave = true) {
    try {
      const service = this.services.find(s => s.id === serviceId);
      if (!service) {
        this.util.showError('Service not found.');
        return;
      }

      const controlName = `service_${service.id}`;
      const rawValue = this.form.get(controlName)?.value;
      const customName = rawValue ? String(rawValue).trim() : '';

      // If custom name is empty, send original name in payload
      if (customName === '') {
        await this.bookJob.updateRequiredTeamServiceName(service.id, service.originalName);
        service.customName = '';
        service.name = service.originalName;
      } else if (customName === service.originalName) {
        // If custom name matches original, delete the custom field (send null)
        await this.bookJob.updateRequiredTeamServiceName(service.id, null);
        service.customName = '';
        service.name = service.originalName;
      } else {
        // Save the custom name
        await this.bookJob.updateRequiredTeamServiceName(service.id, customName);
        service.customName = customName;
        service.name = customName;
      }

      // Show success message for auto-save
      if (isAutoSave) {
        this.util.showSuccess('Changes saved automatically.');
      }
    } catch (err) {
      console.error(err);
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(this.errorMessage);
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

}
